import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Download, Copy, RefreshCw, X, ChevronLeft, ChevronRight, Loader2, Lock, Calendar, Flame, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { T, FMT, prepare, fill, preload, setEstilo } from './engine';
import { loadObraPhotos, loadClima, loadIA, getFeedTimeZone } from './socialStudio.api';

/* Estudio de Redes: vidriera de plantillas que se arman solas con las fotos de la cámara.
   Rota todos los días (azar determinístico por fecha + cámara). */

const PALETAS = [
  { n: 'Lisual', c: '#2BE88B' }, { n: 'Naranja obra', c: '#FF7A00' }, { n: 'Casco', c: '#FFC400' },
  { n: 'Plano', c: '#3D8BFF' }, { n: 'Ladrillo', c: '#FF5B5B' }, { n: 'Hormigón', c: '#E8E4DA' },
];
const CATS = [['todas', 'Todas'], ['avance', 'Avance de obra'], ['reel', 'Reels'], ['story', 'Historias'], ['post', 'Posts'], ['carrusel', 'Carruseles'], ['pdf', 'PDF'], ['timelapse', 'Timelapse']];
const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const TIPO = { reel: 'Reel', story: 'Historia', post: 'Post', carrusel: 'Carrusel', pdf: 'PDF' };
const FONTS = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Playfair+Display:wght@500;700&family=Caveat:wght@500&family=JetBrains+Mono:wght@600&family=Anton&family=Archivo+Black&family=DM+Serif+Display&display=swap';

// ───── azar por día ─────
function rng(seed) { return function () { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
const hash = (s) => [...String(s)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);
function shuffled(arr, r) { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
// ───── variantes: cada pieza de la vidriera es una combinación única ─────
// plantilla × color × tipografía × filtro de foto × encuadre × texto. Cada día recorre un tramo propio de una
// permutación fija de todas las combinaciones: una combinación que ya salió no vuelve a salir nunca.
// Las plantillas se numeran por orden de alta (T sólo crece), así sumar plantillas nuevas no altera lo ya mostrado.
const FAMS = [[null, 'Clásica'], ['Archivo Black', 'Bold'], ['Anton', 'Condensada'], ['DM Serif Display', 'Editorial']];
const FILTROS = [['none', 'Natural'], ['saturate(1.25) contrast(1.08)', 'Vívido'], ['grayscale(1) contrast(1.12)', 'Blanco y negro'], ['sepia(.25) saturate(1.2) brightness(1.03)', 'Cálido'], ['saturate(.85) hue-rotate(-8deg) brightness(1.05)', 'Frío']];
const DXS = [0, -0.12, 0.12];
const SLOTS = 512, DIMS = [SLOTS, PALETAS.length, FAMS.length, FILTROS.length, DXS.length, 3];
const M = DIMS.reduce((a, b) => a * b, 1), PRIMO = 7919, TRAMO = 600;
const EPOCH = Math.floor(Date.UTC(2026, 9, 1) / 864e5);
const dayNumOf = (date) => Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 864e5);
const isoOf = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
function decode(idx) {
  const out = []; for (const d of DIMS) { out.push(idx % d); idx = Math.floor(idx / d); } return out;
}
function variantes(k, feed, okSlot, cuantas, excluir) {
  const salt = Math.abs(hash(feed)) % M, out = [], bases = new Set(excluir || []);
  for (let n = k * TRAMO; n < (k + 1) * TRAMO && out.length < cuantas; n++) {
    const idx = ((((n % M) + M) % M) * PRIMO + salt) % M;
    const [slot, pal, fam, fil, dx, hook] = decode(idx);
    if (!okSlot(slot) || bases.has(slot)) continue;
    bases.add(slot);
    out.push({ slot, key: `${slot}.${pal}.${fam}.${fil}.${dx}.${hook}`, accent: PALETAS[pal].c, estilo: { fam: FAMS[fam][0], filter: FILTROS[fil][0], dx: DXS[dx] }, estiloNombre: `${FILTROS[fil][1]} · ${FAMS[fam][1]}`, hook });
  }
  return out;
}
// historia real día por día desde el arranque (EPOCH), para que ningún diseño base se repita en 3 días seguidos
const HIST = new Map();
function slotsDelDia(k, feed, okSlot, sig) {
  if (k < 0) return variantes(k, feed, okSlot, 6).map((v) => v.slot);
  const key = feed + '|' + sig;
  let h = HIST.get(key);
  if (!h) { h = []; HIST.set(key, h); }
  for (let kk = h.length; kk <= k; kk++) {
    const prev = [h[kk - 1], h[kk - 2], h[kk - 3]].filter(Boolean).flat();
    h.push(variantes(kk, feed, okSlot, 6, prev).map((v) => v.slot));
  }
  return h[k];
}
function dayPlan(date, feed, pool) {
  const ok = new Set(pool.map((t) => T.indexOf(t)));
  const okSlot = (sl) => ok.has(sl);
  const k = dayNumOf(date) - EPOCH;
  // tampoco se repite el diseño base de los 3 días anteriores
  const sig = pool.map((t) => t.id).join(',');
  const previos = [1, 2, 3].map((j) => slotsDelDia(k - j, feed, okSlot, sig)).flat();
  // estrenos: las plantillas dadas de alta en los últimos 2 días salen primero
  const hoyIso = isoOf(date), desde = new Date(date); desde.setDate(desde.getDate() - 2);
  const estrenos = pool.filter((t) => t.alta && t.alta <= hoyIso && t.alta >= isoOf(desde)).map((t) => T.indexOf(t));
  let nov = variantes(k, feed, (sl) => estrenos.includes(sl), Math.min(3, estrenos.length));
  nov = nov.concat(variantes(k, feed, okSlot, 6 - nov.length, previos.concat(nov.map((v) => v.slot))));
  const r = rng(k * 7 + hash(feed));
  const pal = {};
  pool.forEach((t) => { pal[t.id] = r() < 0.4 ? PALETAS[0].c : PALETAS[Math.floor(r() * PALETAS.length)].c; });
  return { k, pal, nov: nov.map((v) => Object.assign(v, { tpl: T[v.slot] })), order: shuffled(pool, rng(k + 1)).map((t) => t.id) };
}

// ───── textos para la publicación ─────
const HOOKS = {
  reel: ['{dias} días de obra en segundos. Así avanza {obra} 🏗️', 'Esto pasa en {obra} mientras no mirás 👀', 'Del primer día a hoy: {obra} no frena.'],
  story: ['Hoy en obra ☀️ {obra} sigue avanzando.', 'Parte del día desde {obra} 📍', '¿Ya viste cómo va {obra}? Te mostramos hoy.'],
  post: ['{obra}, {meses} después. Cada foto es real y la tomó nuestra cámara en obra.', 'La obra que sí se muestra: {obra}.', 'Así se ve {obra} el {hoyLargo}.'],
  carrusel: ['Sin filtros y en tiempo real: así avanza {obra} esta semana 🏗️ Deslizá para ver las capturas →', 'Deslizá para ver cómo cambió {obra} →', 'La historia de {obra}, lámina por lámina →', 'Mes a mes, así avanzó {obra} →'],
  pdf: ['Te compartimos el reporte de avance de {obra}, {mesAnioLargo}.', 'Ficha actualizada de {obra} con fotos reales de hoy.', 'Reporte de obra: {obra} al {hoyLargo}.'],
};
const tag = (s) => '#' + String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]/g, '').toLowerCase();
function caption(tpl, D, v = 0) {
  const h = HOOKS[tpl.tipo] || HOOKS.post;
  return [fill(h[v % h.length], D), '', D.lugar ? `📍 ${D.lugar}` : null, '📸 Imágenes reales de nuestra cámara en obra, 24/7.',
    tpl.tipo === 'pdf' ? 'Pedinos el PDF por mensaje.' : 'Seguí el avance todos los días en nuestras historias.', '',
    [tag(D.obra), tag(D.cliente), '#avancedeobra', '#construccion', '#obra', tpl.cat.includes('timelapse') ? '#timelapse' : '#realestate', '#desarrolloinmobiliario'].join(' ')]
    .filter((l) => l !== null).join('\n');
}

// ───── un solo reloj para todas las miniaturas visibles ─────
const subs = new Set();
let rafId = 0, last = 0;
function tick(now) {
  if (now - last > 50) { last = now; subs.forEach((fn) => fn(now)); }
  rafId = subs.size ? requestAnimationFrame(tick) : 0;
}
function subscribe(fn) { subs.add(fn); if (!rafId) rafId = requestAnimationFrame(tick); return () => subs.delete(fn); }

function drawTpl(canvas, tpl, D, o, t) {
  const ctx = canvas.getContext('2d');
  ctx.save(); ctx.clearRect(0, 0, canvas.width, canvas.height);
  setEstilo(o && o.estilo);
  try { tpl.render(ctx, canvas.width, canvas.height, Math.max(0, t), D, o); } catch (e) { /* foto faltante: se dibuja en el próximo cuadro */ }
  ctx.restore();
}

function Thumb({ tpl, D, o, width = 300, blur }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c || !D) return undefined;
    const [W, H] = FMT[tpl.fmt]; c.width = width; c.height = Math.round(width * H / W);
    const anim = !!(tpl.dur || tpl.slides);
    const dur = tpl.dur || (tpl.slides ? tpl.slides * (tpl.tipo === 'pdf' ? 2.2 : 1.5) : 1);
    const t0 = Math.random() * 3;
    let visible = false, unsub = null;
    const paint = (now) => {
      const t = anim ? ((now / 1000 + t0) % (dur + (tpl.dur ? 1 : 0))) : (tpl.dur ? tpl.dur * 0.8 : 0);
      drawTpl(c, tpl, D, o, tpl.dur ? Math.min(t, tpl.dur) : t);
    };
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible) {
        paint(performance.now());
        if (anim && !blur) unsub = subscribe(paint);
        else { const iv = setInterval(() => paint(performance.now()), 1000); unsub = () => clearInterval(iv); }
      } else if (unsub) { unsub(); unsub = null; }
    }, { rootMargin: '120px' });
    io.observe(c);
    return () => { io.disconnect(); if (unsub) unsub(); };
  }, [tpl, D, o, width, blur]);
  const [W, H] = FMT[tpl.fmt];
  return <canvas ref={ref} className={`w-full h-full block ${blur ? 'blur-sm saturate-50' : ''}`} style={{ aspectRatio: `${W}/${H}` }} />;
}

function fmtLabel(tpl) {
  if (tpl.tipo === 'pdf') return 'PDF';
  if (tpl.slides) return `${tpl.fmt} · ${tpl.slides} láminas`;
  return tpl.dur ? `${tpl.fmt} · ${tpl.dur}s` : tpl.fmt;
}

function Card({ tpl, D, o, badge, onOpen, name, sub, lock, compact }) {
  const Tag = lock ? 'div' : 'button';
  return (
    <Tag type={lock ? undefined : 'button'} onClick={lock ? undefined : onOpen}
      className={`group text-left bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col transition ${lock ? 'opacity-70 cursor-default' : 'hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500'}`}>
      <div className="relative bg-gray-900 flex items-center justify-center">
        <Thumb tpl={tpl} D={D} o={o} blur={!!lock} />
        {badge === 'nuevo' && <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide bg-emerald-500 text-white px-2 py-0.5 rounded-full">NUEVO HOY</span>}
        {badge === 'estreno' && <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide bg-violet-600 text-white px-2 py-0.5 rounded-full">NUEVA</span>}
        {badge === 'trend' && <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wide bg-pink-500 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Flame size={10} />TENDENCIA</span>}
        <span className="absolute bottom-2 right-2 text-[10px] font-semibold bg-black/60 text-white px-1.5 py-0.5 rounded">{fmtLabel(tpl)}</span>
        {lock && <span className="absolute inset-0 flex items-center justify-center gap-1 text-white text-sm font-semibold"><Lock size={14} />{lock}</span>}
      </div>
      {!compact && (
        <div className="p-3">
          <div className="text-sm font-semibold text-gray-800">{name || tpl.nombre}</div>
          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{sub || tpl.desc}</div>
        </div>
      )}
    </Tag>
  );
}

// ───── editor ─────
function Editor({ tpl, D, baseOpts, onClose, onProgramar }) {
  const canvasRef = useRef(null);
  const [titulo, setTitulo] = useState(baseOpts.titulo);
  const [sub, setSub] = useState(baseOpts.sub);
  const [numero, setNumero] = useState(tpl.numero ?? '');
  const [accent, setAccent] = useState(baseOpts.accent);
  const [chip, setChip] = useState(true);
  const [marca, setMarca] = useState(true);
  const [slide, setSlide] = useState(tpl.slides ? 0 : null);
  const [capV, setCapV] = useState(baseOpts.capV || 0);
  const [cap, setCap] = useState(() => caption(tpl, D, baseOpts.capV || 0));
  const [busy, setBusy] = useState('');
  const o = useMemo(() => Object.assign({}, baseOpts, { titulo, sub, numero, accent, chip, marca }, slide != null && tpl.id !== 'grilla-3x3' ? { slide } : {}), [baseOpts, titulo, sub, numero, accent, chip, marca, slide, tpl.id]);
  const oRef = useRef(o); oRef.current = o;

  useEffect(() => {
    const c = canvasRef.current, [W, H] = FMT[tpl.fmt], sc = tpl.fmt.startsWith('A4') ? 0.6 : 0.5;
    c.width = W * sc; c.height = H * sc;
    const t0 = performance.now();
    let id = requestAnimationFrame(function loop(now) {
      const t = Math.max(0, now - t0) / 1000;
      drawTpl(c, tpl, D, oRef.current, tpl.dur ? Math.min(t % (tpl.dur + 1), tpl.dur) : t);
      id = requestAnimationFrame(loop);
    });
    const esc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => { cancelAnimationFrame(id); document.removeEventListener('keydown', esc); };
  }, [tpl, D, onClose]);

  const base = `${D.obra}-${tpl.nombre}`.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
  const download = (blob, name) => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); };
  const full = () => { const [W, H] = FMT[tpl.fmt]; const c = document.createElement('canvas'); c.width = W; c.height = H; return c; };
  const guard = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) { console.error(e); toast.error('No se pudo exportar. Probá de nuevo en unos segundos.'); } finally { setBusy(''); }
  };

  const exportPng = () => guard('Generando imagen…', async () => {
    const c = full(); drawTpl(c, tpl, D, o, tpl.dur ? tpl.dur * 0.8 : 0);
    const b = await new Promise((r) => c.toBlob(r, 'image/png')); download(b, base + '.png'); toast.success('Imagen descargada');
  });
  const exportSlides = () => guard('Descargando láminas…', async () => {
    for (let i = 0; i < tpl.slides; i++) {
      const c = full(); drawTpl(c, tpl, D, Object.assign({}, o, { slide: i, exporting: true }), 0);
      const b = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92)); download(b, `${base}-${i + 1}.jpg`);
      await new Promise((r) => setTimeout(r, 350));
    }
    toast.success(`${tpl.slides} imágenes descargadas`);
  });
  const exportPdf = () => guard('Armando PDF…', async () => {
    const horiz = tpl.fmt === 'A4h', pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: horiz ? 'landscape' : 'portrait' });
    for (let i = 0; i < tpl.slides; i++) {
      const c = full(); drawTpl(c, tpl, D, Object.assign({}, o, { slide: i }), 0);
      if (i) pdf.addPage(); pdf.addImage(c.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, horiz ? 297 : 210, horiz ? 210 : 297);
    }
    download(pdf.output('blob'), base + '.pdf'); toast.success('PDF descargado');
  });
  // Graba la plantilla animada a un video. soloMp4: para redes (Instagram no acepta webm).
  const grabar = (soloMp4) => new Promise((resolve, reject) => {
    const mime = (soloMp4 ? ['video/mp4;codecs=avc1.42E01E', 'video/mp4'] : ['video/mp4;codecs=avc1.42E01E', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm']).find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m));
    if (!mime) { reject(new Error('sin MediaRecorder')); return; }
    const c = full(), ctx = c.getContext('2d'), rec = new MediaRecorder(c.captureStream(30), { mimeType: mime, videoBitsPerSecond: 9e6 }), chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => resolve({ blob: new Blob(chunks, { type: mime.split(';')[0] }), ext: mime.includes('mp4') ? 'mp4' : 'webm' });
    const oo = o; rec.start(); const t0 = performance.now();
    const step = () => {
      const t = Math.max(0, (performance.now() - t0) / 1000);
      setEstilo(oo.estilo); ctx.save(); tpl.render(ctx, c.width, c.height, Math.min(t, tpl.dur), D, oo); ctx.restore();
      setBusy(`Grabando video… ${Math.min(100, Math.round(t / (tpl.dur + 0.4) * 100))}%`);
      if (t < tpl.dur + 0.4) setTimeout(step, 1000 / 30); else rec.stop();
    };
    step();
  });
  const exportVideo = () => guard('Grabando video…', async () => {
    const { blob, ext } = await grabar(false); download(blob, `${base}.${ext}`); toast.success('Video descargado');
  });
  const jpeg = (opts) => { const c = full(); drawTpl(c, tpl, D, opts, 0); return new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92)); };
  // Marketing › Redes sociales: arma los archivos de la pieza y abre "Crear publicación" con el texto.
  const programar = () => guard('Preparando para tus redes…', async () => {
    let archivos;
    if (tpl.dur) {
      try { const { blob } = await grabar(true); archivos = [{ blob, tipo: 'video', ext: 'mp4' }]; } catch (e) {
        const c = full(); drawTpl(c, tpl, D, o, tpl.dur * 0.8);
        archivos = [{ blob: await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92)), tipo: 'foto', ext: 'jpg' }];
        toast.info('Este navegador no graba MP4: va la portada como foto. Desde Chrome actualizado sale el video.');
      }
    } else if (tpl.slides && tpl.id !== 'grilla-3x3') {
      archivos = [];
      for (let i = 0; i < Math.min(tpl.slides, 10); i++) archivos.push({ blob: await jpeg(Object.assign({}, o, { slide: i, exporting: true })), tipo: 'foto', ext: 'jpg' });
    } else {
      archivos = [{ blob: await jpeg(o), tipo: 'foto', ext: 'jpg' }];
    }
    onProgramar({ archivos, texto: cap, nombre: tpl.nombre, formato: tpl.fmt, historia: tpl.tipo === 'story', reel: !!tpl.dur && tpl.tipo === 'reel' });
    onClose();
  });

  const actions = tpl.dur ? [['Exportar video', exportVideo, true], ['Descargar portada (imagen)', exportPng]]
    : tpl.tipo === 'pdf' ? [['Descargar PDF', exportPdf, true]]
      : tpl.slides ? [[`Descargar las ${tpl.slides} imágenes`, exportSlides, true]] : [['Descargar imagen', exportPng, true]];

  const copy = async () => { try { await navigator.clipboard.writeText(cap); toast.success('Texto copiado'); } catch (e) { toast.info('Seleccioná el texto y copialo'); } };
  const input = 'block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm leading-6 text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label={tpl.nombre}>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[92vh] overflow-hidden grid grid-rows-[minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-[minmax(0,1fr)_360px]">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50" aria-label="Cerrar"><X size={18} /></button>
        <div className="bg-gray-100 flex flex-col items-center justify-center gap-3 p-4 md:p-6 min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 w-full flex items-center justify-center">
            <canvas ref={canvasRef} className="block rounded-xl shadow-lg" style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }} />
          </div>
          {tpl.slides && tpl.id !== 'grilla-3x3' && (
            <div className="flex gap-1.5 flex-wrap justify-center">
              {Array.from({ length: tpl.slides }, (_, i) => (
                <button key={i} type="button" onClick={() => setSlide(i)} className={`text-xs px-2.5 py-1 rounded-lg border ${slide === i ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700'}`}>{tpl.tipo === 'pdf' ? 'Página' : 'Lámina'} {i + 1}</button>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 overflow-y-auto min-h-0 flex flex-col gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">{TIPO[tpl.tipo]} · {tpl.fmt}</div>
            <h3 className="text-xl font-bold text-gray-800 mt-1">{tpl.nombre}</h3>
            <p className="text-sm text-gray-500">{tpl.desc}</p>
          </div>
          <label className="text-xs font-medium text-gray-600" htmlFor="ss-titulo">Título</label>
          <textarea id="ss-titulo" rows={3} className={`${input} resize-y`} style={{ height: 96, minHeight: 72, fontSize: 14, lineHeight: 1.5 }} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          <label className="text-xs font-medium text-gray-600" htmlFor="ss-sub">Texto secundario</label>
          <input id="ss-sub" className={input} style={{ height: 42, fontSize: 14 }} value={sub} onChange={(e) => setSub(e.target.value)} />
          {tpl.numero != null && (<>
            <label className="text-xs font-medium text-gray-600" htmlFor="ss-num">Número</label>
            <input id="ss-num" type="number" className={input} style={{ height: 42, fontSize: 14 }} value={numero} onChange={(e) => setNumero(e.target.value)} />
          </>)}
          <div className="text-xs font-medium text-gray-600">Color</div>
          <div className="flex gap-2">
            {PALETAS.map((p) => <button key={p.c} type="button" title={p.n} aria-pressed={accent === p.c} onClick={() => setAccent(p.c)} className={`w-8 h-8 rounded-full border-2 ${accent === p.c ? 'border-gray-800' : 'border-white shadow'}`} style={{ background: p.c }} />)}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={chip} onChange={(e) => setChip(e.target.checked)} /> Nombre de la empresa</label>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={marca} onChange={(e) => setMarca(e.target.checked)} /> Sello "cámara en vivo"</label>
          <div className="flex flex-col gap-2 mt-1">
            {actions.map(([l, fn, primary]) => (
              <button key={l} type="button" disabled={!!busy} onClick={fn} className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60 ${primary ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-50'}`}>
                {busy && primary ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}{busy && primary ? busy : l}
              </button>
            ))}
            {onProgramar && tpl.tipo !== 'pdf' && (
              <button type="button" disabled={!!busy} onClick={programar} className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60 bg-gray-900 hover:bg-black text-white">
                {busy === 'Preparando para tus redes…' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}Programar en mis redes
              </button>
            )}
          </div>
          <label className="text-xs font-medium text-gray-600 mt-2" htmlFor="ss-cap">Texto para la publicación</label>
          <textarea id="ss-cap" rows={9} className={`${input} resize-y whitespace-pre-wrap`} style={{ height: 220, minHeight: 160, fontSize: 14, lineHeight: 1.55 }} value={cap} onChange={(e) => setCap(e.target.value)} />
          <div className="flex gap-2">
            <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"><Copy size={14} />Copiar texto</button>
            <button type="button" onClick={() => { const v = capV + 1; setCapV(v); setCap(caption(tpl, D, v)); }} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw size={14} />Otra versión</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───── pantalla principal ─────
export default function SocialStudio({ user, customerInfo, identifier, onProgramar }) {
  const feedID = user?.feedID;
  const [D, setD] = useState(null);
  const [state, setState] = useState({ loading: true, found: 0, error: '' });
  const [dayOff, setDayOff] = useState(0);
  const [cat, setCat] = useState('todas');
  const [open, setOpen] = useState(null);
  const rowRef = useRef(null);
  const closeEditor = useCallback(() => setOpen(null), []);
  const ctxRef = useRef({ user, customerInfo }); ctxRef.current = { user, customerInfo };
  const identifierRef = useRef(identifier); identifierRef.current = identifier;

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONTS}"]`)) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = FONTS; document.head.appendChild(l); }
  }, []);

  useEffect(() => {
    if (!feedID) return undefined;
    let alive = true;
    setState({ loading: true, found: 0, error: '' }); setD(null);
    (async () => {
      const { user, customerInfo } = ctxRef.current;
      try {
        const [photos] = await Promise.all([
          loadObraPhotos(feedID, user?.customerId, (n, total) => alive && setState((s) => ({ ...s, found: n, total }))),
          document.fonts ? Promise.race([Promise.all(['700 40px Poppins', '500 40px Poppins', '700 40px "Playfair Display"', '500 30px Caveat', '600 20px "JetBrains Mono"', '400 40px Anton', '400 40px "Archivo Black"', '400 40px "DM Serif Display"'].map((f) => document.fonts.load(f))).catch(() => null), new Promise((r) => setTimeout(r, 3000))]) : null,
        ]);
        if (!alive) return;
        if (photos.hist.length < 2) { setState({ loading: false, found: photos.hist.length, error: 'pocas' }); return; }
        // se muestra enseguida; las fotos aparecen a medida que llegan (como mucho 4 s de espera)
        await Promise.race([preload(photos.hist.slice(0, 3).concat(photos.hist.slice(-1)).map((h) => h.src)), new Promise((r) => setTimeout(r, 4000))]);
        preload(photos.hist.map((h) => h.src).concat(photos.hoy.map((h) => h.src)));
        const lat = user?.coordinates?.lat, lng = user?.coordinates?.lng;
        const raw = {
          feed: feedID,
          obra: user?.obraName || user?.Name || 'Tu obra',
          cliente: customerInfo?.name || 'Tu empresa',
          ig: customerInfo?.instagram ? `@${String(customerInfo.instagram).replace(/^@/, '')}` : '',
          lugar: user?.city || customerInfo?.city || '',
          logoSrc: customerInfo?.logo || user?.logo || '',
          ...photos,
        };
        if (raw.logoSrc) await Promise.race([preload([raw.logoSrc]), new Promise((r) => setTimeout(r, 2500))]);
        const data = prepare(raw, null);
        if (!alive) return;
        setD(data); setState({ loading: false, found: photos.hist.length, error: '' });
        const [clima, ia] = await Promise.all([loadClima(lat, lng), getFeedTimeZone(feedID).then((tz) => loadIA(identifierRef.current, user?.customerId, 14, tz))]);
        if (alive && (clima || ia)) setD((d) => (d ? Object.assign({}, d, clima ? { clima } : {}, ia ? { ia } : {}) : d));
      } catch (e) {
        console.error(e);
        if (alive) setState({ loading: false, found: 0, error: 'red' });
      }
    })();
    return () => { alive = false; };
  }, [feedID, customerInfo?.name]);

  const vDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + dayOff); return d; }, [dayOff]);
  const disponibles = useMemo(() => T.filter((t) => !t.requires || (D && D[t.requires] && D[t.requires].dias && D[t.requires].dias.length)), [D]);
  const plan = useMemo(() => dayPlan(vDate, feedID || '', disponibles), [vDate, feedID, disponibles]);
  const optsFor = useCallback((t) => (D ? { accent: plan.pal[t.id], titulo: fill(t.titulo, D), sub: fill(t.sub, D), numero: t.numero, chip: true, marca: true } : null), [D, plan]);
  const optsCache = useMemo(() => { const m = {}; if (D) T.forEach((t) => { m[t.id] = optsFor(t); }); return m; }, [D, optsFor]);
  const byId = (id) => T.find((t) => t.id === id);
  const optsVar = (v) => (D ? { accent: v.accent, estilo: v.estilo, titulo: fill(v.tpl.titulo, D), sub: fill(v.tpl.sub, D), numero: v.tpl.numero, chip: true, marca: true, capV: v.hook } : null);
  const novIds = plan.nov.map((v) => v.tpl.id);
  const semana = useMemo(() => {
    const out = []; for (let i = 1; out.length < 5 && i < 12; i++) { const d = new Date(vDate); d.setDate(d.getDate() + i); if (d.getDay() % 6) out.push({ d, v: dayPlan(d, feedID || '', disponibles).nov[0] }); }
    return out;
  }, [vDate, feedID, disponibles]);
  const manana = useMemo(() => { const d = new Date(vDate); d.setDate(d.getDate() + 1); return dayPlan(d, feedID || '', disponibles).nov.slice(0, 4); }, [vDate, feedID, disponibles]);
  const badgeOf = (t) => (novIds.includes(t.id) ? 'nuevo' : t.nuevo ? 'estreno' : t.trend ? 'trend' : null);
  const grid = plan.order.map(byId).filter((t) => cat === 'todas' || t.cat.includes(cat) || t.tipo === cat);
  const scrollRow = (dir) => rowRef.current && rowRef.current.scrollBy({ left: dir * 480, behavior: 'smooth' });

  if (!feedID) return <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600">Esta cámara no tiene fotos asociadas.</div>;

  if (state.loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center gap-3 text-gray-700">
          <Loader2 className="animate-spin text-blue-600" size={20} />
          <div className="flex-1">
            <div className="font-semibold">Armando tu contenido con las fotos de la cámara…</div>
            {state.total ? <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-md"><div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round(state.found / state.total * 100)}%` }} /></div> : null}
            <div className="text-sm text-gray-500">{state.total ? `Revisando la historia de la obra: ${state.found} de ${state.total}` : 'Buscando las fotos de hoy'}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="aspect-[9/16] rounded-2xl bg-gray-200 animate-pulse" />)}
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-700">
        <div className="font-semibold">{state.error === 'pocas' ? 'Todavía no hay suficientes fotos para armar contenido' : 'No pudimos traer las fotos de la cámara'}</div>
        <div className="text-sm text-gray-500 mt-1">{state.error === 'pocas' ? 'Cuando la cámara junte unos días de fotos vas a ver acá reels, historias y posts listos para publicar.' : 'Revisá la conexión y volvé a abrir la pestaña.'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Encabezado */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"><Sparkles size={13} />Estudio de redes · beta</div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1" title="Simulá otro día para ver cómo rota la vidriera">
          <button type="button" onClick={() => setDayOff(dayOff - 1)} className="w-8 h-8 rounded-lg hover:bg-gray-50 flex items-center justify-center" aria-label="Día anterior"><ChevronLeft size={16} /></button>
          <span className="text-xs font-medium text-gray-700 min-w-[110px] text-center inline-flex items-center justify-center gap-1"><Calendar size={13} />{dayOff === 0 ? 'Hoy' : `${DIAS[vDate.getDay()].slice(0, 3)} ${vDate.getDate()}/${vDate.getMonth() + 1}`}</span>
          <button type="button" onClick={() => setDayOff(dayOff + 1)} className="w-8 h-8 rounded-lg hover:bg-gray-50 flex items-center justify-center" aria-label="Día siguiente"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Novedades */}
      <section>
        <div className="flex items-end justify-between gap-4 mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Novedades de hoy</h3>
            <p className="text-sm text-gray-500">Seleccionadas para el {DIAS[vDate.getDay()].toLowerCase()} {vDate.getDate()}/{vDate.getMonth() + 1}. Mañana cambian.</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button type="button" onClick={() => scrollRow(-1)} className="w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center" aria-label="Anterior"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => scrollRow(1)} className="w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center" aria-label="Siguiente"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={rowRef} className="flex gap-4 overflow-x-auto snap-x pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {plan.nov.map((v) => (
            <div key={v.key} className="w-[200px] shrink-0 snap-start"><Card tpl={v.tpl} D={D} o={optsVar(v)} badge={v.tpl.alta ? 'estreno' : 'nuevo'} sub={`${v.estiloNombre} · ${v.tpl.desc}`} onOpen={() => setOpen({ tpl: v.tpl, o: optsVar(v) })} /></div>
          ))}
        </div>
      </section>

      {/* Semana */}
      <section>
        <h3 className="text-lg font-bold text-gray-800">Tu semana, lista para publicar</h3>
        <p className="text-sm text-gray-500 mb-3">Un contenido por día con su texto. Tocá para editar y descargar.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {semana.map(({ d, v }) => (
            <button key={v.key + '-' + d.getDate()} type="button" onClick={() => setOpen({ tpl: v.tpl, o: optsVar(v) })} className="text-left bg-white border border-gray-200 rounded-2xl p-3 hover:border-blue-300 hover:shadow-md transition flex flex-col gap-2 min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">{DIAS[d.getDay()]} {d.getDate()}/{d.getMonth() + 1}</div>
              <div className="rounded-xl overflow-hidden bg-gray-900"><Thumb tpl={v.tpl} D={D} o={optsVar(v)} width={260} /></div>
              <div className="text-xs text-gray-500 line-clamp-3">{caption(v.tpl, D, v.hook).split('\n')[0]}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Todas */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-gray-800">Todas las plantillas</h3>
          <div className="flex flex-wrap gap-1.5" role="tablist">
            {CATS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={cat === k} onClick={() => setCat(k)} className={`text-sm px-3.5 py-1.5 rounded-full border ${cat === k ? 'bg-blue-600 border-blue-600 text-white font-semibold' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {grid.map((t) => <Card key={t.id} tpl={t} D={D} o={optsCache[t.id]} badge={badgeOf(t)} onOpen={() => setOpen({ tpl: t, o: optsCache[t.id] })} />)}
        </div>
      </section>

      {/* Próximamente */}
      <section>
        <h3 className="text-lg font-bold text-gray-800">Llegan mañana</h3>
        <p className="text-sm text-gray-500 mb-3">Cada día se suman plantillas nuevas.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl">
          {manana.map((v) => <Card key={v.key} tpl={v.tpl} D={D} o={optsVar(v)} lock="Mañana" sub={v.estiloNombre} />)}
        </div>
      </section>

      {open && <Editor tpl={open.tpl} D={D} baseOpts={open.o} onClose={closeEditor} onProgramar={onProgramar} />}
    </div>
  );
}
